import { useState, useMemo, memo } from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CategoryIcon from '@mui/icons-material/Category'
import {
  SchedulerResourceAndMaterialConstraintsRowType,
  SchedulerResourceAndInputRowType,
  SchedulerResourceAndOutputMappingRowType,
  SchedulerTabsProps,
} from './Scheduler.types'
import { useScheduleProductionRequirements } from './Scheduler.data'

interface ResourceCard {
  resource: string
  constraints: SchedulerResourceAndMaterialConstraintsRowType[]
  inputs: SchedulerResourceAndInputRowType[]
  outputs: SchedulerResourceAndOutputMappingRowType[]
}

const ACCENT_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#c62828',
  '#00838f',
  '#4e342e',
  '#37474f',
]

const getAccentColor = (index: number): string =>
  ACCENT_COLORS[index % ACCENT_COLORS.length]

const ProductListItem = memo(
  ({ code, runRate }: { code: string; runRate: number }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.5,
        px: 1,
        '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
      }}
    >
      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
        {code}
      </Typography>
      <Chip
        label={`${runRate} /hr`}
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    </Box>
  )
)

ProductListItem.displayName = 'ProductListItem'

const ResourceCardComponent = memo(
  ({ card, accentColor }: { card: ResourceCard; accentColor: string }) => {
    const hasConstraints = card.constraints.length > 0
    const hasInputs = card.inputs.length > 0
    const hasOutputs = card.outputs.length > 0

    return (
      <Paper
        variant="outlined"
        sx={{
          borderLeft: `4px solid ${accentColor}`,
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon sx={{ color: accentColor, fontSize: '1.2rem' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
            {card.resource}
          </Typography>
        </Box>

        {hasConstraints ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {card.constraints.map((c) => (
              <Chip
                key={c.id}
                icon={
                  <WarningAmberIcon sx={{ fontSize: '0.85rem !important' }} />
                }
                label={`${c.ConstraintType}: ${c.ConstraintAmount} ${c.UOM}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            ))}
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            No constraints defined
          </Typography>
        )}

        {(hasInputs || hasOutputs) && <Divider sx={{ my: 0.25 }} />}

        {hasInputs && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              bgcolor: 'transparent',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
              sx={{
                minHeight: 28,
                px: 0.5,
                '& .MuiAccordionSummary-content': { my: 0.25 },
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                Input Products ({card.inputs.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {card.inputs.map((input) => (
                <ProductListItem
                  key={input.id}
                  code={input.inputProductCode}
                  runRate={input.runRate}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {hasOutputs && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              bgcolor: 'transparent',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
              sx={{
                minHeight: 28,
                px: 0.5,
                '& .MuiAccordionSummary-content': { my: 0.25 },
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                Output Products ({card.outputs.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {card.outputs.map((output) => (
                <ProductListItem
                  key={output.id}
                  code={output.productCode}
                  runRate={output.runRate}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
    )
  }
)

ResourceCardComponent.displayName = 'ResourceCardComponent'

export const ScheduleConstraintCardsView = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const [search, setSearch] = useState('')

  const {
    data: constraintsResponse = { data: [] },
    isLoading: constraintsLoading,
  } = useScheduleProductionRequirements(
    schedulerId,
    'resource-and-material-constraints',
    iteration
  )

  const { data: inputsResponse = { data: [] }, isLoading: inputsLoading } =
    useScheduleProductionRequirements(
      schedulerId,
      'resource-and-input-mapping',
      iteration
    )

  const { data: outputsResponse = { data: [] }, isLoading: outputsLoading } =
    useScheduleProductionRequirements(
      schedulerId,
      'resource-and-output-mapping',
      iteration
    )

  const isLoading = constraintsLoading || inputsLoading || outputsLoading

  const constraints: SchedulerResourceAndMaterialConstraintsRowType[] =
    constraintsResponse?.data ?? []
  const inputs: SchedulerResourceAndInputRowType[] = inputsResponse?.data ?? []
  const outputs: SchedulerResourceAndOutputMappingRowType[] =
    outputsResponse?.data ?? []

  const resourceCards = useMemo<ResourceCard[]>(() => {
    const resourceMap = new Map<string, ResourceCard>()

    const getOrCreate = (resource: string): ResourceCard => {
      if (!resourceMap.has(resource)) {
        resourceMap.set(resource, {
          resource,
          constraints: [],
          inputs: [],
          outputs: [],
        })
      }
      return resourceMap.get(resource)!
    }

    constraints.forEach((c) => {
      getOrCreate(c.ResourceConstraint).constraints.push(c)
    })
    inputs.forEach((i) => {
      getOrCreate(i.resource).inputs.push(i)
    })
    outputs.forEach((o) => {
      getOrCreate(o.resource).outputs.push(o)
    })

    const cards = Array.from(resourceMap.values())
    cards.sort((a, b) => a.resource.localeCompare(b.resource))
    return cards
  }, [constraints, inputs, outputs])

  const filteredCards = useMemo(() => {
    if (!search.trim()) return resourceCards
    const term = search.toLowerCase()
    return resourceCards.filter((card) => {
      if (card.resource.toLowerCase().includes(term)) return true
      if (
        card.constraints.some((c) =>
          c.ConstraintType.toLowerCase().includes(term)
        )
      )
        return true
      if (
        card.inputs.some((i) => i.inputProductCode.toLowerCase().includes(term))
      )
        return true
      if (card.outputs.some((o) => o.productCode.toLowerCase().includes(term)))
        return true
      return false
    })
  }, [resourceCards, search])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (resourceCards.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <Typography color="text.secondary">
          No resource data available for this schedule.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <TextField
        size="small"
        placeholder="Search resources, constraints, or products..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: '100%', maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{ fontSize: '1.2rem', color: 'text.secondary' }}
              />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment
              position="end"
              sx={{ cursor: 'pointer' }}
              onClick={() => setSearch('')}
            >
              <ClearIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
            </InputAdornment>
          ) : null,
        }}
      />

      {filteredCards.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No resources match &quot;{search}&quot;
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: '1fr 1fr 1fr',
            },
            gap: 2,
          }}
        >
          {filteredCards.map((card, index) => (
            <ResourceCardComponent
              key={card.resource}
              card={card}
              accentColor={getAccentColor(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
